import * as React from "react";
import { LogOut, User, ChevronRight } from "lucide-react";
import { Menu, X } from "lucide-react"; // Menu icon for hamburger
import { twMerge } from "tailwind-merge";
import { useAuth } from "../../hooks/useAuth";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchNavItems,
  selectVisibleNavItems,
  selectIsMenuExpanded,
  toggleSubmenu,
  handleNavItemClick,
} from "../../store/navigationSlice";
import useOutsideClick from "../../hooks/useOutsideClick";

// Helper function to render the proper icon
function getIcon(item) {
  if (!item.icon_file) return null;

  // Handle icon positioning
  const iconClasses = twMerge(
    "h-4 w-4",
    item.icon_position === "right" ? "ml-auto" : ""
  );

  // Check if it's a Lucide icon or an image file
  if (
    item.icon_file.endsWith(".png") ||
    item.icon_file.endsWith(".jpg") ||
    item.icon_file.endsWith(".svg")
  ) {
    return (
      <img
        src={`/icons/${item.icon_file}`}
        alt={`${item.display} icon`}
        className={iconClasses}
      />
    );
  }

  // For Lucide icons, we would need a mapping from icon names to components
  // This is a placeholder - you would need to implement the actual mapping
  return null;
}

function NavItem({ item, nestingLevel = 0 }) {
  const dispatch = useDispatch();
  const location = useLocation();
  const isExpanded = useSelector((state) =>
    selectIsMenuExpanded(state, item.glid)
  );

  // Fallback mapping for items with missing action_arg
  const fallbackRoutes = {
    Pages: "Pages_All",
    "Market Products": "MarketProduct_All",
    Ads: "MarketAds_All",
  };

  // Get the final action_arg (use fallback if original is empty)
  const finalActionArg =
    item.action_arg && item.action_arg.trim() !== ""
      ? item.action_arg
      : fallbackRoutes[item.display];

  // Determine if this should be treated as a navigation item
  const shouldNavigate =
    (item.action === "goto_page" && finalActionArg) ||
    (finalActionArg && (item.action === "" || !item.action));

  // Determine if this item is active based on its action_arg matching the current path
  const isActive = shouldNavigate && location.pathname === `/${finalActionArg}`;

  // Calculate if the item is disabled
  const isDisabled = item.state === "disable";

  // Handle separator case
  if (item.action === "seperator") {
    return (
      <div className="my-2">
        <div className="h-px bg-gray-300 mx-4"></div>
      </div>
    );
  }

  // this is to prevent double rendering of the logout button
  if (item.name === "Logout") return null;

  // Common classes for both Link and div
  const commonClasses = twMerge(
    `flex items-center gap-3 px-4 py-2 rounded-lg transition-colors text-sm`,
    `ml-${nestingLevel * 2}`, // Indentation based on nesting level
    isActive
      ? "bg-white text-blue-600 shadow-sm"
      : "text-gray-600 hover:bg-white hover:text-blue-600",
    isDisabled && "opacity-50 cursor-not-allowed",
    !isDisabled && "cursor-pointer"
  );

  // Common styles for both Link and div
  const commonStyles = item.bg_color
    ? {
        backgroundColor: item.bg_color,
        color: item.text_color || "white",
      }
    : {};

  // Common content for both Link and div
  const content = (
    <>
      {getIcon(item)}
      <span className="font-medium">{item.display}</span>
      {item.action === "open_sub_menu" && (
        <ChevronRight
          className={`ml-auto h-4 w-4 transition-transform ${
            isExpanded ? "rotate-90" : ""
          }`}
        />
      )}
    </>
  );

  return (
    <div className="space-y-1">
      {shouldNavigate ? (
        // Render Link for navigation items (including fallback routes)
        <Link
          to={`/${finalActionArg}`}
          className={commonClasses}
          style={commonStyles}
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
            }
          }}
        >
          {content}
        </Link>
      ) : (
        // Render div for submenu items or items without action_arg
        <div
          onClick={() => {
            if (!isDisabled && item.action === "open_sub_menu") {
              dispatch(toggleSubmenu(item.glid));
            }
          }}
          className={commonClasses}
          style={commonStyles}
        >
          {content}
        </div>
      )}

      {/* Render children if this is an expanded submenu */}
      {item.action === "open_sub_menu" && isExpanded && item.children && (
        <div className="ml-4 space-y-1">
          {item.children.map((childId) => (
            <ConnectedNavItem
              key={childId}
              id={childId}
              nestingLevel={nestingLevel + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Connected component to fetch the item from the store by ID
function ConnectedNavItem({ id, nestingLevel }) {
  const items = useSelector(selectVisibleNavItems);
  // Normalize the id to handle both dot and hyphen formats
  const normalizedId = id.replace("-", ".");
  const item = items.find((item) => {
    // Normalize the item's glid for comparison
    const normalizedGlid = item.glid?.replace("-", ".");
    return normalizedGlid === normalizedId;
  });

  if (!item) return null;

  return <NavItem item={item} nestingLevel={nestingLevel} />;
}

function Sidebar({ children }) {
  const { logout } = useAuth();
  const [isOpen, setIsOpen] = React.useState(false);
  const dispatch = useDispatch();

  const navItems = useSelector(selectVisibleNavItems);
  const userInformation = useSelector(
    (state) => state.navigation.userInformation
  );
  const loading = useSelector((state) => state.navigation.loading);
  const error = useSelector((state) => state.navigation.error);

  // Load nav items on mount
  React.useEffect(() => {
    dispatch(fetchNavItems());
  }, [dispatch]);

  // Close sidebar when clicking outside (on small screens only)
  const sidebarRef = useOutsideClick(() => {
    if (isOpen) setIsOpen(false);
  }, isOpen);

  const rootItems = navItems.filter((item) => item.level === 0);

  const LoadingSkeleton = () => (
    <div className="space-y-6 p-6">
      {[1, 2, 3].map((i) => (
        <div key={i} className="space-y-4">
          <div className="h-6 bg-gray-200 rounded-md animate-pulse w-3/4"></div>
          <div className="space-y-3 ml-4">
            {[1, 2, 3].map((j) => (
              <div
                key={j}
                className="h-4 bg-gray-200 rounded-md animate-pulse w-2/3"
              ></div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden">
      {/* Sidebar and Hamburger */}
      <div
        ref={sidebarRef}
        className={`fixed lg:static top-0 left-0 z-40 w-[230px] h-screen bg-gray-100 flex flex-col transition-transform duration-300 ease-in-out transform ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        {/* Top: Logo + Close icon (small screens only) */}
        <div className="flex items-center justify-between p-6 pb-2">
          <img
            src="/GearLAB_logo.png"
            alt="GearLAB Logo"
            className="w-[100px] h-[50px] md:w-auto md:h-auto md:max-w-full md:max-h-[60px]"
          />
        </div>

        {/* Sidebar content with responsive text */}
        <div className="flex-1 h-screen overflow-y-auto px-4 py-2 text-xs sm:text-xs smd:text-sm md:text-base lg:text-[15px] xl:text-base 1xl:text-lg 2xl:text-xl">
          {loading ? (
            <LoadingSkeleton />
          ) : error ? (
            <div className="text-red-500 p-4">
              Failed to load navigation: {error.message}
            </div>
          ) : (
            <nav className="space-y-4">
              {rootItems.map((item) => (
                <React.Fragment key={item.glid || `root-${item.display}`}>
                  <NavItem item={item} />
                </React.Fragment>
              ))}
            </nav>
          )}
        </div>

        {/* User Profile */}
        {/* User Profile + Logout (also small text) */}
        <div className="px-6 pt-2 text-xs sm:text-xs smd:text-sm md:text-base lg:text-[15px] xl:text-base 1xl:text-lg 2xl:text-xl">
          {userInformation && (
            <UserProfile
              userName={userInformation.username}
              type={userInformation.user_type}
            />
          )}
        </div>
        {/* Logout */}
        <div className="px-6 pb-6 pt-2 text-xs sm:text-xs smd:text-sm md:text-base lg:text-[15px] xl:text-base 1xl:text-lg 2xl:text-xl">
          <button
            onClick={logout}
            className="flex items-center gap-3 px-4 py-2 rounded-lg cursor-pointer w-full bg-red-600 text-white hover:bg-red-700"
          >
            <LogOut className="h-4 w-4" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </div>

      {/* Content + Toggle Button (hamburger inline) */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Inline hamburger (only on small screens) */}
        <div className="lg:hidden p-4 bg-white shadow-sm">
          <div className="flex justify-between">
            <button onClick={() => setIsOpen(true)} className="text-gray-800">
              <Menu className="h-6 w-6" />
            </button>
            {isOpen && (
              <button onClick={() => setIsOpen(false)}>
                <X className="h-6 w-6" />
              </button>
            )}
          </div>
        </div>

        {/* Main content */}
        <main className="flex-1 overflow-hidden p-6 bg-white">
          <div className="w-full h-full overflow-auto">{children}</div>
        </main>
      </div>
    </div>
  );
}

function UserProfile({ userName, userGlid, sessionNumber, type }) {
  return (
    <div className="bg-white rounded-lg p-4 shadow-sm mb-2">
      <div className="flex items-center gap-3">
        <div className="bg-blue-100 p-2 rounded-full">
          <User className="text-blue-600 h-5 w-5" />
        </div>
        <div className="flex flex-col">
          <p className="font-medium text-gray-900 text-[14px] truncate">
            {userName}
          </p>
          <p className="text-xs text-gray-500 truncate">
            {/* {userGlid} • {sessionNumber} • {type} */}
            {type}
          </p>
        </div>
      </div>
    </div>
  );
}

export default Sidebar;
